<script lang="ts" setup generic="T extends object = Record<string, unknown>, TContext = unknown">
import type { Component } from 'vue';
import type { FormSchema, FormSchemaField, FormSchemaFieldActual, FormSchemaResolved } from '#layers/block-engine/types/form-schema';
import type { FormSchemaAdapter } from '#layers/block-engine/utils/form-schema';
import { computed } from 'vue';

import { callOrReturn } from '#layers/base';
import { resolveFormSchema } from '#layers/block-engine/utils/form-schema';
import { useTranslationUtils } from '#layers/i18n';

const props = defineProps<{
	schema: FormSchema<T, TContext>;
	adapter: FormSchemaAdapter<T>;
	context?: TContext;
	transformSchema?: (schema: FormSchemaResolved<T>) => FormSchemaResolved<T>;
}>();

defineSlots<{
	'field-hint': (props: { field: FormSchemaFieldActual<T, string> }) => unknown;
	'field': (props: { field: FormSchemaFieldActual<T, string> }) => unknown;
	'field-render': (props: {
		field: FormSchemaFieldActual<T, string>;
		render: Component;
		value: unknown;
		setValue: (value: unknown) => void;
	}) => unknown;
}>();

const { translateString } = useTranslationUtils();

/**
 * Forward a field write to the adapter that owns the form values.
 */
function handleUpdate(field: FormSchemaFieldActual<T, string>, value: unknown) {
	props.adapter.set({
		field,
		value,
	});
}

const resolvedSchema = computed(() => {
	const values = props.adapter.getValues();

	if (typeof props.schema === 'function') {
		return resolveFormSchema({
			schema: props.schema,
			transformSchema: props.transformSchema,
			context: (props.context ?? {}) as TContext,
			values,
		});
	}

	return resolveFormSchema({
		schema: props.schema,
		transformSchema: props.transformSchema,
		values,
	});
});
</script>

<template>
	<template
		v-for="(field, index) of resolvedSchema"
	>
		<DFormSeparator
			v-if="field.type === 'separator'"
			:key="index"
			:label="translateString(field.label)"
		/>

		<template
			v-else-if="field.type === 'group'"
		>
			<DDisclosure
				v-if="field.fields.some((f: FormSchemaField<T>) => !callOrReturn(f.hidden))"
				:key="index"
				:label="translateString(field.label)"
				:default-open="field.defaultOpen"
				:disabled="field.collapsible === false"
				class="col-span-full"
				:unmount-on-hide="false"
			>
				<DFormLayout class="my-4">
					<FormSchemaFields
						:schema="field.fields"
						:adapter
						:context
						:transform-schema
					/>
				</DFormLayout>
			</DDisclosure>
		</template>

		<FormSchemaField
			v-else-if="!callOrReturn(field.hidden)"
			:key="field.name"
			:field
			:adapter
			@set-value="(value) => handleUpdate(field, value)"
		>
			<template #hint>
				<slot
					name="field-hint"
					:field
				/>
			</template>

			<template #default>
				<slot
					name="field"
					:field
				/>
			</template>

			<template #render="slotProps">
				<slot
					name="field-render"
					:field
					v-bind="slotProps"
				/>
			</template>
		</FormSchemaField>
	</template>
</template>
